import { useMemo, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router';
import {
  Bell,
  BellOff,
  Check,
  ChevronDown,
  ChevronRight,
  Compass,
  Copy,
  FolderPlus,
  Hash,
  Hand,
  Headphones,
  HeadphoneOff,
  Images,
  Link,
  Lock,
  LogOut,
  Megaphone,
  MessagesSquare,
  Mic,
  MicOff,
  MonitorUp,
  Plus,
  Radio,
  Settings,
  Trash2,
  UserPlus,
  Video,
  Volume2,
} from 'lucide-react';
import { hasPermission, Permission, type Channel, type VoiceState } from '@spawnwire/shared';
import { Avatar, Badge, clsx, ContextMenu, DropdownMenu, IconButton, type MenuEntry } from '../../components/ui';
import { http } from '../../lib/api';
import { ackChannel, attempt, markSpaceRead } from '../../lib/data';
import { copyText, useAppRoute } from '../../lib/hooks';
import { collapsed as collapsedStore } from '../../lib/local';
import { can, channelPermissions, useSpacePermissions } from '../../lib/perms';
import { publicOrigin } from '../../lib/platform';
import { joinVoice, useVoice } from '../../lib/voice';
import { channelsOfSpace, displayNameIn, isMuted, isUnread, mentionCount, useStore } from '../../store/store';
import { ui } from '../../store/ui';

export function channelIcon(channel: Pick<Channel, 'type'>, isPrivate: boolean) {
  switch (channel.type) {
    case 'voice':
      return isPrivate ? Lock : Volume2;
    case 'stage':
      return Radio;
    case 'announcement':
      return Megaphone;
    case 'forum':
      return MessagesSquare;
    case 'media':
      return Images;
    default:
      return isPrivate ? Lock : Hash;
  }
}

/** Channels you join like a call rather than open to read. */
export const isCallChannel = (c: Pick<Channel, 'type'>) => c.type === 'voice' || c.type === 'stage';

/** A channel is "private" in the UI when @everyone cannot view it. */
export function isPrivateChannel(channel: Channel, everyoneRoleId: string | undefined, parent?: Channel): boolean {
  const deniesView = (c?: Channel) => !!c?.overwrites.some((o) => o.targetId === everyoneRoleId && hasPermission(o.deny, Permission.VIEW_CHANNEL));
  const allowsView = channel.overwrites.some((o) => o.targetId === everyoneRoleId && hasPermission(o.allow, Permission.VIEW_CHANNEL));
  return deniesView(channel) || (deniesView(parent) && !allowsView);
}

function VoiceParticipants({ channelId, spaceId, stage }: { channelId: string; spaceId: string; stage?: boolean }) {
  const states = useStore((s) => s.voiceStates[channelId]);
  const users = useStore((s) => s.users);
  const speaking = useVoice((v) => v.speaking);
  const all = Object.values(states ?? {}).sort((a, b) => a.joinedAt.localeCompare(b.joinedAt));
  // On a stage only the speakers are listed; the audience is a count.
  const list = stage ? all.filter((v) => !v.suppress) : all;
  const audience = all.length - list.length;
  if (all.length === 0) return null;
  return (
    <ul className="mb-1 ml-7 space-y-0.5" aria-label={stage ? 'Speakers on this stage' : 'People in this channel'}>
      {list.map((v: VoiceState) => (
        <li key={v.userId} className="flex items-center gap-2 rounded px-1.5 py-0.5 text-sm text-muted hover:bg-hover">
          <Avatar user={users[v.userId]} size={22} speaking={!!speaking[v.userId] && !v.selfMute && !v.serverMute} />
          <span className="min-w-0 flex-1 truncate">{displayNameIn(useStore.getState(), v.userId, spaceId)}</span>
          {v.screen && <MonitorUp className="size-3.5 text-danger" aria-label="Sharing screen" />}
          {v.video && <Video className="size-3.5" aria-label="Camera on" />}
          {(v.selfMute || v.serverMute) && <MicOff className={clsx('size-3.5', v.serverMute && 'text-danger')} aria-label={v.serverMute ? 'Server muted' : 'Muted'} />}
          {(v.selfDeaf || v.serverDeaf) && <HeadphoneOff className={clsx('size-3.5', v.serverDeaf && 'text-danger')} aria-label="Deafened" />}
          {stage && <Mic className="size-3.5 text-success" aria-label="Speaker" />}
        </li>
      ))}
      {stage && audience > 0 && (
        <li className="flex items-center gap-2 px-1.5 py-0.5 text-xs text-faint">
          <Hand className="size-3.5" aria-hidden /> {audience} listening
        </li>
      )}
    </ul>
  );
}

interface DragState {
  id: string;
  type: Channel['type'];
}

export function SpaceSidebar({ spaceId }: { spaceId: string }) {
  const navigate = useNavigate();
  const { channelId: activeId } = useAppRoute();
  const space = useStore((s) => s.spaces[spaceId]);
  const allChannels = useStore((s) => s.channels);
  // Subscribed so unread, mute and permission changes re-render the rows (which read the store directly).
  useStore((s) => s.readStates);
  useStore((s) => s.notificationSettings);
  useStore((s) => s.myMembers[spaceId]);
  const perms = useSpacePermissions(spaceId);
  const [collapsed, setCollapsed] = useState(() => collapsedStore.get());
  const [drag, setDrag] = useState<DragState | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; where: 'before' | 'after' | 'into' } | null>(null);
  const voiceChannelId = useVoice((v) => v.channelId);

  const everyoneId = space?.roles.find((r) => r.isDefault)?.id;
  const manage = can(perms, Permission.MANAGE_CHANNELS);

  const groups = useMemo(() => {
    const list = channelsOfSpace({ channels: allChannels }, spaceId);
    const categories = list.filter((c) => c.type === 'category');
    const children = (parentId: string | null) => list.filter((c) => c.type !== 'category' && (c.parentId ?? null) === parentId);
    return [{ category: null as Channel | null, channels: children(null) }, ...categories.map((c) => ({ category: c, channels: children(c.id) }))];
  }, [allChannels, spaceId]);

  if (!space) return null;

  const toggle = (id: string) => setCollapsed(new Set(collapsedStore.toggle(id)));

  function openChannel(c: Channel) {
    if (isCallChannel(c)) void joinVoice(c.id);
    navigate(`/app/${spaceId}/${c.id}`);
    ui.setMobilePane('main');
  }

  function channelMenu(c: Channel): MenuEntry[] {
    const state = useStore.getState();
    const cperms = channelPermissions(state, c);
    const muted = isMuted(state, c.id);
    return [
      { label: 'Mark as read', icon: Check, hidden: isCallChannel(c) || c.type === 'category', onSelect: () => ackChannel(c) },
      {
        label: muted ? 'Unmute channel' : 'Mute channel',
        icon: muted ? Bell : BellOff,
        hidden: c.type === 'category',
        onSelect: () => void attempt(() => http.put(`/users/@me/notification-settings/${c.id}`, { mutedUntil: muted ? null : new Date(Date.now() + 365 * 86400_000).toISOString() })),
      },
      { label: 'Invite people', icon: UserPlus, hidden: c.type === 'category' || !can(cperms, Permission.CREATE_INVITE), onSelect: () => ui.open({ kind: 'invite', spaceId, channelId: c.id }) },
      { label: 'Copy link', icon: Link, hidden: c.type === 'category', onSelect: () => void copyText(`${publicOrigin()}/app/${spaceId}/${c.id}`) },
      { label: 'Notification settings', icon: Bell, hidden: can(cperms, Permission.MANAGE_CHANNELS), onSelect: () => ui.open({ kind: 'channelSettings', channelId: c.id }) },
      { label: c.type === 'category' ? 'Edit category' : 'Edit channel', icon: Settings, hidden: !can(cperms, Permission.MANAGE_CHANNELS), separatorBefore: true, onSelect: () => ui.open({ kind: 'channelSettings', channelId: c.id }) },
      {
        label: c.type === 'category' ? 'Delete category' : 'Delete channel',
        icon: Trash2,
        danger: true,
        hidden: !can(cperms, Permission.MANAGE_CHANNELS),
        onSelect: () =>
          ui.confirm({
            title: `Delete ${c.type === 'category' ? c.name : `#${c.name}`}?`,
            body: c.type === 'category' ? 'Channels inside it will move to the top of the list.' : 'All of its messages and threads will be permanently deleted.',
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: () => http.del(`/channels/${c.id}`),
          }),
      },
      { label: 'Copy channel ID', icon: Copy, onSelect: () => void copyText(c.id) },
    ];
  }

  // ---- Drag and drop reordering (MANAGE_CHANNELS) ----------------------------------

  function onDragOver(e: DragEvent, target: Channel) {
    if (!drag || drag.id === target.id) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const top = e.clientY < rect.top + rect.height / 2;
    if (target.type === 'category' && drag.type !== 'category') setDropTarget({ id: target.id, where: 'into' });
    else if ((target.type === 'category') === (drag.type === 'category')) setDropTarget({ id: target.id, where: top ? 'before' : 'after' });
  }

  async function onDrop(e: DragEvent) {
    e.preventDefault();
    const d = drag;
    const t = dropTarget;
    setDrag(null);
    setDropTarget(null);
    if (!d || !t) return;
    const state = useStore.getState();
    const list = channelsOfSpace(state, spaceId);
    const target = list.find((c) => c.id === t.id)!;
    const moving = list.find((c) => c.id === d.id)!;
    let order: { id: string; position: number; parentId?: string | null }[];
    if (moving.type === 'category') {
      const cats = list.filter((c) => c.type === 'category' && c.id !== moving.id);
      const idx = cats.findIndex((c) => c.id === target.id) + (t.where === 'after' ? 1 : 0);
      cats.splice(idx, 0, moving);
      order = cats.map((c, i) => ({ id: c.id, position: i }));
    } else {
      const parentId = t.where === 'into' ? target.id : (target.parentId ?? null);
      const siblings = list.filter((c) => c.type !== 'category' && (c.parentId ?? null) === parentId && c.id !== moving.id);
      const idx = t.where === 'into' ? siblings.length : siblings.findIndex((c) => c.id === target.id) + (t.where === 'after' ? 1 : 0);
      siblings.splice(idx, 0, moving);
      order = siblings.map((c, i) => ({ id: c.id, position: i, ...(c.id === moving.id ? { parentId } : {}) }));
    }
    await attempt(() => http.put(`/spaces/${spaceId}/channels/order`, order));
  }

  function row(c: Channel) {
    const state = useStore.getState();
    const parent = c.parentId ? state.channels[c.parentId] : undefined;
    const priv = isPrivateChannel(c, everyoneId, parent);
    const Icon = channelIcon(c, priv);
    const active = activeId === c.id;
    const unread = !isCallChannel(c) && isUnread(state, c) && !isMuted(state, c.id);
    const mentions = mentionCount(state, c.id);
    const muted = isMuted(state, c.id);
    const cperms = channelPermissions(state, c);
    const marker = dropTarget?.id === c.id ? dropTarget.where : null;
    return (
      <li
        key={c.id}
        draggable={manage}
        onDragStart={(e) => {
          setDrag({ id: c.id, type: c.type });
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => {
          setDrag(null);
          setDropTarget(null);
        }}
        onDragOver={(e) => onDragOver(e, c)}
        onDrop={onDrop}
        className="relative"
      >
        {marker === 'before' && <span className="absolute -top-px left-2 right-2 h-0.5 rounded bg-accent" aria-hidden />}
        {marker === 'after' && <span className="absolute -bottom-px left-2 right-2 h-0.5 rounded bg-accent" aria-hidden />}
        <ContextMenu entries={channelMenu(c)}>
          <div
            className={clsx(
              'group relative mx-2 flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2 text-[15px]',
              active ? 'bg-active text-fg' : unread ? 'text-fg hover:bg-hover' : 'text-muted hover:bg-hover hover:text-fg',
              muted && !active && 'opacity-50',
              drag?.id === c.id && 'opacity-40',
            )}
            onClick={() => openChannel(c)}
            role="link"
            tabIndex={0}
            aria-current={active ? 'page' : undefined}
            onKeyDown={(e) => e.key === 'Enter' && openChannel(c)}
          >
            {unread && !active && <span className="absolute -left-2 h-2 w-1 rounded-r-full bg-fg" aria-hidden />}
            <Icon className="size-[18px] shrink-0 opacity-70" aria-hidden />
            <span className={clsx('min-w-0 flex-1 truncate', unread && 'font-semibold')}>{c.name}</span>
            {unread && <span className="sr-only">(unread)</span>}
            {voiceChannelId === c.id && <Headphones className="size-3.5 text-success" aria-label="Connected" />}
            <Badge count={mentions} />
            {can(cperms, Permission.CREATE_INVITE) && !isCallChannel(c) && (
              <button
                type="button"
                aria-label={`Invite to ${c.name}`}
                className="hidden rounded p-0.5 text-muted hover:text-fg group-hover:block"
                onClick={(e) => {
                  e.stopPropagation();
                  ui.open({ kind: 'invite', spaceId, channelId: c.id });
                }}
              >
                <UserPlus className="size-4" />
              </button>
            )}
            {can(cperms, Permission.MANAGE_CHANNELS) && (
              <button
                type="button"
                aria-label={`Edit ${c.name}`}
                className={clsx('rounded p-0.5 text-muted hover:text-fg', active ? 'block' : 'hidden group-hover:block')}
                onClick={(e) => {
                  e.stopPropagation();
                  ui.open({ kind: 'channelSettings', channelId: c.id });
                }}
              >
                <Settings className="size-4" />
              </button>
            )}
          </div>
        </ContextMenu>
        {isCallChannel(c) && <VoiceParticipants channelId={c.id} spaceId={spaceId} stage={c.type === 'stage'} />}
      </li>
    );
  }

  const isOwner = space.ownerId === useStore.getState().me?.id;
  const firstText = groups.flatMap((g) => g.channels).find((c) => c.type === 'text');
  const headerMenu: MenuEntry[] = [
    { label: 'Invite people', icon: UserPlus, hidden: !can(perms, Permission.CREATE_INVITE) || !firstText, onSelect: () => firstText && ui.open({ kind: 'invite', spaceId, channelId: firstText.id }) },
    { label: 'Space settings', icon: Settings, onSelect: () => ui.open({ kind: 'spaceSettings', spaceId }) },
    { label: 'Create channel', icon: Plus, hidden: !manage, onSelect: () => ui.open({ kind: 'createChannel', spaceId, parentId: null }) },
    { label: 'Create category', icon: FolderPlus, hidden: !manage, onSelect: () => ui.open({ kind: 'createChannel', spaceId, parentId: null, type: 'category' }) },
    { label: 'Mark all as read', icon: Check, separatorBefore: true, onSelect: () => markSpaceRead(spaceId) },
    {
      label: 'Leave space',
      icon: LogOut,
      danger: true,
      hidden: isOwner,
      separatorBefore: true,
      onSelect: () =>
        ui.confirm({
          title: `Leave ${space.name}?`,
          body: 'You will need a new invite to rejoin.',
          confirmLabel: 'Leave space',
          danger: true,
          onConfirm: async () => {
            await http.del(`/spaces/${spaceId}/members/@me`);
            navigate('/app/@me');
          },
        }),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DropdownMenu
        align="start"
        entries={headerMenu}
        trigger={
          space.bannerUrl ? (
            // With a banner, the name sits over the image like Discord's boosted header.
            <button type="button" className="group relative h-32 shrink-0 overflow-hidden border-b border-line text-left font-semibold text-white">
              <img src={space.bannerUrl} alt="" className="absolute inset-0 size-full object-cover" draggable={false} />
              <span className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/70 to-transparent" aria-hidden />
              <span className="absolute inset-x-0 top-0 flex h-12 items-center justify-between gap-2 px-4 group-hover:bg-black/20">
                <span className="flex min-w-0 items-center gap-1.5 drop-shadow">
                  {space.discovery === 'approved' && <Compass className="size-4 shrink-0" aria-label="Listed on Discover" />}
                  <span className="truncate">{space.name}</span>
                </span>
                <ChevronDown className="size-4 shrink-0 drop-shadow" aria-hidden />
              </span>
            </button>
          ) : (
            <button type="button" className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-line px-4 text-left font-semibold text-fg shadow-sm hover:bg-hover">
              <span className="flex min-w-0 items-center gap-1.5">
                {space.discovery === 'approved' && <Compass className="size-4 shrink-0 text-accent" aria-label="Listed on Discover" />}
                <span className="truncate">{space.name}</span>
              </span>
              <ChevronDown className="size-4 shrink-0" aria-hidden />
            </button>
          )
        }
      />
      <nav aria-label={`${space.name} channels`} className="scroll-thin min-h-0 flex-1 overflow-y-auto py-3" onDragOver={(e) => drag && e.preventDefault()}>
        {groups.map(({ category, channels }) => {
          const isCollapsed = category ? collapsed.has(category.id) : false;
          const visible = isCollapsed ? channels.filter((c) => c.id === activeId || (!isCallChannel(c) && isUnread(useStore.getState(), c))) : channels;
          if (!category && channels.length === 0) return null;
          return (
            <section key={category?.id ?? 'root'} className="mb-3">
              {category && (
                <ContextMenu entries={channelMenu(category)}>
                  <div
                    className={clsx('group relative flex items-center pl-1 pr-3', dropTarget?.id === category.id && 'rounded bg-accent-soft')}
                    draggable={manage}
                    onDragStart={(e) => {
                      setDrag({ id: category.id, type: 'category' });
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragEnd={() => {
                      setDrag(null);
                      setDropTarget(null);
                    }}
                    onDragOver={(e) => onDragOver(e, category)}
                    onDrop={onDrop}
                  >
                    {dropTarget?.id === category.id && dropTarget.where === 'before' && <span className="absolute -top-1 left-2 right-2 h-0.5 rounded bg-accent" aria-hidden />}
                    <button
                      type="button"
                      onClick={() => toggle(category.id)}
                      aria-expanded={!isCollapsed}
                      className="flex min-w-0 flex-1 items-center gap-0.5 py-1 text-xs font-semibold uppercase tracking-wide text-faint hover:text-muted"
                    >
                      {isCollapsed ? <ChevronRight className="size-3.5" aria-hidden /> : <ChevronDown className="size-3.5" aria-hidden />}
                      <span className="truncate">{category.name}</span>
                    </button>
                    {can(channelPermissions(useStore.getState(), category), Permission.MANAGE_CHANNELS) && (
                      <IconButton icon={Plus} label="Create channel" size="sm" className="opacity-0 group-hover:opacity-100 focus:opacity-100" onClick={() => ui.open({ kind: 'createChannel', spaceId, parentId: category.id })} />
                    )}
                  </div>
                </ContextMenu>
              )}
              <ul className="mt-0.5 space-y-px">{visible.map(row)}</ul>
            </section>
          );
        })}
      </nav>
    </div>
  );
}
